import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  X,
} from 'lucide-react';
import clsx from 'clsx';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

/** An optional button on the toast (e.g. DAT C5 "Remove from DAT"). Clicking it also closes the toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastMessage {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: ToastAction;
}

interface ToastProps extends ToastMessage {
  onClose: (id: string) => void;
}

// Equipment hotfix (PR #886) added the most complete dark-mode token
// set — bg + border + text + icon all flip in dark theme. The Customer
// quickfix branch added text + icon dark variants only. Develop's
// version is a strict superset (same idea, more complete coverage), so
// take it whole. Same goal: each toast keeps its color identity but
// renders with high contrast on dark surfaces (the "Customer list
// exported" success-toast legibility case is covered by the
// `dark:text-green-100` line).
const toastConfig = {
  success: {
    bg: 'bg-green-50 dark:bg-green-950',
    border: 'border-green-200 dark:border-green-700',
    text: 'text-green-800 dark:text-green-100',
    icon: CheckCircle,
    iconColor: 'text-green-600 dark:text-green-300',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950',
    border: 'border-red-200 dark:border-red-700',
    text: 'text-red-800 dark:text-red-100',
    icon: AlertCircle,
    iconColor: 'text-red-600 dark:text-red-300',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950',
    border: 'border-blue-200 dark:border-blue-700',
    text: 'text-blue-800 dark:text-blue-100',
    icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-300',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900',
    border: 'border-yellow-200 dark:border-yellow-700',
    text: 'text-yellow-800 dark:text-yellow-100',
    icon: AlertTriangle,
    iconColor: 'text-yellow-600 dark:text-yellow-300',
  },
};

export function Toast({ id, message, type, action, onClose }: ToastProps) {
  const config = toastConfig[type];
  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={clsx(
        'flex items-start gap-3 rounded-lg border px-4 py-3 max-w-md',
        config.bg,
        config.border,
        config.text
      )}
    >
      <Icon className={clsx('h-5 w-5 flex-shrink-0 mt-0.5', config.iconColor)} />
      {/* `whitespace-pre-line` keeps explicit newlines in the message (so
          multi-line warnings render line-by-line) while still wrapping long
          lines; `break-words` prevents long ids from overflowing the toast. */}
      <p className="flex-1 text-sm font-medium whitespace-pre-line break-words">{message}</p>
      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            onClose(id);
          }}
          className="flex-shrink-0 rounded border border-current px-2 py-0.5 text-xs font-semibold hover:opacity-80"
          data-testid="toast-action"
        >
          {action.label}
        </button>
      )}
      <button
        onClick={() => onClose(id)}
        className="flex-shrink-0 text-current opacity-70 hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
      <AnimatePresence>
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast {...toast} onClose={onClose} />
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
